import ApplicationController from "./application_controller"
import {
  LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY,
  attachLexiqamusOttomanKeyboardDrag,
  mapLexiqamusKeyboardEventToOttomanChar
} from "./lexiqamus_search/ottoman_keyboard"
import { isOttomanText } from "../lib/ottoman_text_utils"
// Manages the split search bar: left half = Latin mode, right half = Ottoman mode.
// OTT/ENG toggle: OTT uses BuildLQ dictionary search; ENG loads LexiQamus English definitions in an iframe.
// Placeholders and border theme (navy vs burgundy) switch based on active side and input.
// Ottoman (right) click opens the grouped Ottoman helper keyboard (LexiQamus layout).
export default class extends ApplicationController {
  static targets = [
    "wrapper",
    "input",
    "placeholderContainer",
    "placeholderEnglish",
    "placeholderLatin",
    "placeholderOttoman",
    "searchTypeField",
    "searchButton",
    "actionButtons",
    "shareLinkButton",
    "sourceSwitch",
    "sourceSwitchOption",
    "deniedMessage",            // Shown when allow_search blocks Search (Word Decoder stays open)
    "ottomanSideKeyboard",      // Ottoman keyboard panel element
    "ottomanKeyboardDragHandle" // Drag handle for repositioning keyboard
  ]

  static values = {
    placeholderEnglish: String,
    isTurkish: Boolean,
    newSearchUrl: String,
    copiedText: String
  }

  // Binds document/window listeners used by the Ottoman keyboard.
  initialize() {
    this.onOttomanKeyboardDocumentMouseDown = this.onOttomanKeyboardDocumentMouseDown.bind(this)
    this.onOttomanKeyboardWindowResize = this.onOttomanKeyboardWindowResize.bind(this)
    this.onOttomanKeyboardWindowScroll = this.onOttomanKeyboardWindowScroll.bind(this)
  }

  // Restores bar state, wires Ottoman keyboard drag, and syncs OTT/ENG switch UI.
  connect() {
    this.activeMode = null // 'latin' | 'ottoman' | null
    this.sourceType = "ott" // 'ott' | 'eng'
    // Clear any saved drag position on page load so the keyboard opens in the default spot after refresh.
    // Dragging within the same session still persists until the next page load.
    this.clearSavedOttomanKeyboardDragPosition()
    // Shared ENG links (?search_type=definition) reopen the iframe search; otherwise infer latin/ottoman from the input.
    const restoredEngDefinitionSearch = this.restoreEngDefinitionSearchFromUrl()
    if (!restoredEngDefinitionSearch) {
      this.syncFromInput()
    }
    this.updatePlaceholders()
    this.applySearchButtonTheme()
    if (this.hasInputTarget) {
      this.updateSearchFieldFont(this.inputTarget)
    }

    // Register global event listeners for Ottoman keyboard
    document.addEventListener("mousedown", this.onOttomanKeyboardDocumentMouseDown)
    window.addEventListener("resize", this.onOttomanKeyboardWindowResize)
    window.addEventListener("scroll", this.onOttomanKeyboardWindowScroll, true)
    this.syncOttomanSideKeyboardAriaHidden()
    // Initialize drag functionality for Ottoman keyboard
    this.removeOttomanKeyboardDrag = null
    if (this.hasOttomanSideKeyboardTarget && this.hasOttomanKeyboardDragHandleTarget) {
      this.removeOttomanKeyboardDrag = attachLexiqamusOttomanKeyboardDrag(
        this.ottomanSideKeyboardTarget,
        this.ottomanKeyboardDragHandleTarget
      )
    }
    this.updateSourceSwitchUI()
    this.updateActionButtonsVisibility()
    this.focusSearchInput()
  }

  // CPG-263: Focus Latin search on open/refresh so users can type without clicking first.
  // Applies to LexiQamus and BuildLQ simple search (both mount this controller).
  // Skips restricted Search (allow_search) so we do not surface the denial message on load.
  focusSearchInput() {
    if (!this.hasInputTarget || this.isSearchBarRestricted()) return
    // Word Decoder reload keeps the search panel hidden; skip focus so the hidden bar does not steal focus on load.
    if (this.isWordDecoderTabActive()) return

    // Empty bar has no inferred mode yet — default to Latin (left side) before focusing.
    if (this.activeMode === null && this.sourceType !== "eng") {
      this.activeMode = "latin"
      this.applyInputDirection()
      this.applyTheme()
      this.updatePlaceholders()
      this.updateSearchTypeField()
      this.applySearchButtonTheme()
    }

    this.inputTarget.focus()
  }

  // Cleanup event listeners and drag functionality on disconnect
  disconnect() {
    this.clearOttomanSearchOnScreenKeyboard()
    if (this.shareLinkResetTimeoutId) {
      clearTimeout(this.shareLinkResetTimeoutId)
      this.shareLinkResetTimeoutId = null
    }
    document.removeEventListener("mousedown", this.onOttomanKeyboardDocumentMouseDown)
    window.removeEventListener("resize", this.onOttomanKeyboardWindowResize)
    window.removeEventListener("scroll", this.onOttomanKeyboardWindowScroll, true)
    if (this.removeOttomanKeyboardDrag) {
      this.removeOttomanKeyboardDrag()
      this.removeOttomanKeyboardDrag = null
    }
  }

  // Checks if Ottoman keyboard panel is currently visible
  isOttomanKeyboardOpen() {
    return this.hasOttomanSideKeyboardTarget && this.ottomanSideKeyboardTarget.classList.contains("js-lexiqamus-search-keyboard--open")
  }

  // Updates aria-hidden attribute for accessibility based on keyboard visibility
  syncOttomanSideKeyboardAriaHidden() {
    if (!this.hasOttomanSideKeyboardTarget) return
    this.ottomanSideKeyboardTarget.setAttribute("aria-hidden", this.isOttomanKeyboardOpen() ? "false" : "true")
  }

  // Retrieves saved Ottoman keyboard position from localStorage
  readSavedOttomanKeyboardDragPosition() {
    try {
      const raw = window.localStorage.getItem(LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (parsed && parsed.top != null && parsed.left != null) return parsed
    } catch (_e) {
      // ignore
    }
    return null
  }

  // Clears saved Ottoman keyboard drag position from localStorage
  clearSavedOttomanKeyboardDragPosition() {
    try {
      window.localStorage.removeItem(LEXIQAMUS_OTTOMAN_KEYBOARD_DRAG_KEY)
    } catch (_e) {
      // ignore
    }
  }

  // Applies saved drag position to Ottoman keyboard panel
  applySavedOttomanKeyboardDragPosition() {
    const saved = this.readSavedOttomanKeyboardDragPosition()
    if (!saved || !this.hasOttomanSideKeyboardTarget) return false
    const kbd = this.ottomanSideKeyboardTarget
    kbd.style.top = saved.top
    kbd.style.left = saved.left
    kbd.style.right = "auto"
    kbd.style.transform = "none"
    return true
  }

  // CPG-425: Place keyboard under the search bar, lined up with the right side of the text box (Ottoman side).
  // Runs when the keyboard opens and on window resize. Does not move while the user types.
  positionOttomanSideKeyboard() {
    if (!this.hasOttomanSideKeyboardTarget || !this.hasWrapperTarget || !this.hasInputTarget) return
    if (this.applySavedOttomanKeyboardDragPosition()) return // user dragged it; keep their position

    const pad = 8 // stay at least 8px away from the left/right edge of the window
    const keyboardPanel = this.ottomanSideKeyboardTarget
    const wrapperRect = this.wrapperTarget.getBoundingClientRect()
    const inputRect = this.inputTarget.getBoundingClientRect()
    const paddingRight = parseFloat(window.getComputedStyle(this.inputTarget).paddingRight) || 0
    const anchorX = inputRect.right - paddingRight // right side of the text area (inside the box padding)
    const panelW = keyboardPanel.offsetWidth || 0
    // Middle of keyboard on that spot; if it would go off-screen, shift it so it still fits.
    const left = Math.max(pad, Math.min(anchorX - panelW / 2, window.innerWidth - panelW - pad))

    keyboardPanel.style.left = `${left}px`
    keyboardPanel.style.top = `${wrapperRect.bottom + 10}px`
    keyboardPanel.style.right = "auto"
    keyboardPanel.style.transform = "none"
  }

  // Show keyboard, then place it on the next frame (after the browser knows how wide it is).
  showOttomanSideKeyboard() {
    if (!this.hasOttomanSideKeyboardTarget) return
    this._ottomanPageScrollEnabled = false
    this.ottomanSideKeyboardTarget.classList.add("lexiqamus-search-keyboard--open", "js-lexiqamus-search-keyboard--open")
    this.syncOttomanSideKeyboardAriaHidden()
    requestAnimationFrame(() => {
      this.positionOttomanSideKeyboard()
      this.ensureOttomanSideKeyboardVisible()
      this.clearOttomanSideKeyboardEnsureVisibleTimer()
      // Retry once keyboard width/height and landing layout have settled.
      this._ottomanSideKeyboardEnsureVisibleTimer = setTimeout(() => this.ensureOttomanSideKeyboardVisible(), 320)
    })
  }

  // Closes Ottoman keyboard panel
  hideOttomanSideKeyboard() {
    if (!this.hasOttomanSideKeyboardTarget) return
    this.ottomanSideKeyboardTarget.classList.remove(
      "lexiqamus-search-keyboard--open",
      "js-lexiqamus-search-keyboard--open",
      "lexiqamus-search-keyboard--needs-page-scroll"
    )
    this.syncOttomanSideKeyboardAriaHidden()
    this._ottomanPageScrollEnabled = false
    this.clearOttomanSideKeyboardEnsureVisibleTimer()
    const spacer = document.getElementById("gridKbdSpacer")
    if (spacer) spacer.style.height = "0px"
  }

  // Fixed keyboard sits below the search bar; if its bottom edge is clipped, scroll the page
  // so the full panel is reachable. Mirrors word-decoder ensureKeyboardVisible (PR #2140).
  ensureOttomanSideKeyboardVisible() {
    if (!this.isOttomanKeyboardOpen()) return

    // How many px the keyboard bottom extends past the viewport (keep 10px breathing room).
    const bottomOverflowPx = this.ottomanSideKeyboardTarget.getBoundingClientRect().bottom - (window.innerHeight - 10)
    if (bottomOverflowPx <= 0) return

    // Landing stays locked on open (footer hidden via CSS); unlock only once scroll is actually needed.
    this.releaseOttomanKeyboardHomeScrollLock()

    // When the document is already scrolled to the bottom, grow #gridKbdSpacer so scrollBy has room.
    const spacer = document.getElementById("gridKbdSpacer")
    if (spacer) {
      const maxNaturalScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      const extraSpacerHeight = (window.scrollY + bottomOverflowPx) - maxNaturalScrollY
      if (extraSpacerHeight > 0) {
        const current = parseInt(spacer.style.height || "0", 10) || 0
        spacer.style.height = `${current + Math.ceil(extraSpacerHeight + 20)}px`
      }
    }
    window.scrollBy({ top: Math.ceil(bottomOverflowPx), behavior: "auto" })
  }

  // Unlock home viewport + page scroll once the keyboard actually extends below the viewport.
  releaseOttomanKeyboardHomeScrollLock() {
    if (this._ottomanPageScrollEnabled) return
    this._ottomanPageScrollEnabled = true
    this.ottomanSideKeyboardTarget.classList.add("lexiqamus-search-keyboard--needs-page-scroll")

    // Landing locks scroll until the user clicks Discover (search_output#enableLandingInnovationsScroll).
    // When the keyboard bottom overflows the viewport, ensureOttomanSideKeyboardVisible must scroll the
    // page — that only works once these locks are released. Safe to touch here because:
    //   • Runs once per open (_ottomanPageScrollEnabled guard above), only after real overflow.
    //   • Clearing inline minHeight/height drops syncHomeViewportToWindowHeight's pin; CSS + its resize
    //     listener in lexiqamus_announcements.js can re-apply on window resize — same as Discover unlock.
    //   • Removing scroll-lock classes mirrors enableLandingInnovationsScroll; we do not re-lock on hide
    //     (user already needed scroll — same UX as clicking Discover).
    const home = document.querySelector(".lexiqamus-home-viewport")
    if (home) {
      home.style.minHeight = ""
      home.style.height = ""
    }
    document.documentElement.classList.remove("lexiqamus-innovations-scroll", "lexiqamus-landing-scroll-locked")
    document.body.classList.remove("lexiqamus-landing-scroll-locked")
  }

  // Resets open state, timer, and spacer (disconnect + hide share this path).
  clearOttomanSearchOnScreenKeyboard() {
    this.hideOttomanSideKeyboard()
  }

  // showOttomanSideKeyboard schedules a 320ms retry after the first rAF because panel
  // dimensions and landing layout can still shift (fonts, footer hide). Clear it on hide,
  // disconnect, and before rescheduling so a stale retry cannot scroll after close.
  clearOttomanSideKeyboardEnsureVisibleTimer() {
    if (!this._ottomanSideKeyboardEnsureVisibleTimer) return
    clearTimeout(this._ottomanSideKeyboardEnsureVisibleTimer)
    this._ottomanSideKeyboardEnsureVisibleTimer = null
  }

  // Closes Ottoman keyboard when clicking outside the keyboard panel and search input wrapper.
  // Mirrors lexiqamus-word-decoder-keyboard: only the keyboard itself and its trigger area
  // (the split input wrapper) keep the keyboard open — clicking anywhere else closes it.
  onOttomanKeyboardDocumentMouseDown(event) {
    if (!this.isOttomanKeyboardOpen()) return
    if (this.hasOttomanSideKeyboardTarget && this.ottomanSideKeyboardTarget.contains(event.target)) return
    if (this.hasWrapperTarget && this.wrapperTarget.contains(event.target)) return
    this.hideOttomanSideKeyboard()
  }

  // Window size changed: default position again (unless the user dragged the keyboard).
  onOttomanKeyboardWindowResize() {
    if (!this.isOttomanKeyboardOpen()) return
    if (!this.readSavedOttomanKeyboardDragPosition()) this.positionOttomanSideKeyboard()
    this.ensureOttomanSideKeyboardVisible()
  }

  // Keeps the fixed keyboard above Discover 2026 when scrolling (CPG-386).
  // Applies both to the default position and the dragged position.
  onOttomanKeyboardWindowScroll() {
    if (!this.isOttomanKeyboardOpen()) return
    
    const keyboardElement = this.ottomanSideKeyboardTarget
    let newTop = null

    // Check if user has dragged the keyboard
    const savedDragPos = this.readSavedOttomanKeyboardDragPosition()
    if (savedDragPos && savedDragPos.top) {
      // Use the explicitly dragged position as the base
      newTop = parseFloat(savedDragPos.top)
    } else {
      // Use the default position (just below the search input)
      const wrapperRect = this.wrapperTarget.getBoundingClientRect()
      newTop = wrapperRect.bottom + 10
    }
    
    const panelHeight = keyboardElement.offsetHeight || 0
    const viewportPad = 10

    // Prevent overlap with Discover cue when it is visible (hidden via CSS while keyboard is open).
    const innovationsEl = document.querySelector(".lexiqamus-scroll-indicator-wrapper")
    if (innovationsEl) {
      const innovationsRect = innovationsEl.getBoundingClientRect()
      // height > 0: cue is display:none while keyboard is open; skip when already hidden.
      // Overlap check: if keyboard bottom would cross the cue top, lift the panel above it.
      if (innovationsRect.height > 0 && newTop + panelHeight + viewportPad > innovationsRect.top) {
        newTop = innovationsRect.top - panelHeight - viewportPad
      }
    }

    // maxTop = lowest top value that still leaves viewportPad below the panel bottom.
    // Clamp newTop so a lifted panel (Discover cue) or tall keyboard never clips off-screen.
    const maxTop = Math.max(viewportPad, window.innerHeight - panelHeight - viewportPad)
    newTop = Math.max(viewportPad, Math.min(newTop, maxTop))

    keyboardElement.style.top = `${newTop}px`
  }

  // Inserts Ottoman character at current cursor position in input field
  insertOttomanCharAtCaret(ch) {
    if (!ch) return
    const input = this.inputTarget
    const start = input.selectionStart
    const end = input.selectionEnd
    input.value = input.value.slice(0, start) + ch + input.value.slice(end)
    input.setSelectionRange(start + ch.length, start + ch.length)
    input.dispatchEvent(new Event("input"))
    input.focus()
  }

  // Handles on-screen keyboard key clicks
  ottomanSearchKeyboardKeyMouseDown(event) {
    event.preventDefault()
    const ch = event.currentTarget.getAttribute("data-mapped-value")
    if (!ch) return
    this.lightOttomanKey(event.currentTarget)
    this.insertOttomanCharAtCaret(ch)
  }

  // Handles physical keyboard input in Ottoman mode (maps keys to Ottoman characters)
  handleSearchInputKeydown(event) {
    if (this.sourceType === "eng") return
    if (this.activeMode !== "ottoman") return
    if (event.ctrlKey || event.metaKey) return
    if (event.isComposing) return

    const key = event.key
    // Allow navigation and control keys to pass through
    if (
      [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
        "Enter",
        "Escape"
      ].includes(key)
    ) {
      return
    }

    // Script-producing physical keyboards (Osmanlıca, Arabic, Urdu) already supply the
    // correct Ottoman/Arabic character in event.key — do not replace with LexiQamus
    // on-screen layout (e.g. Osmanlıca KeyC → ج must not become LexiQamus KeyC → چ).
    // Refrence ticket: https://evolving-bits.atlassian.net/jira/software/projects/CPG/boards/1?selectedIssue=CPG-179
    if (key.length === 1 && isOttomanText(key)) return

    const ch = mapLexiqamusKeyboardEventToOttomanChar({
      key: event.key,
      code: event.code,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      isTurkishLocale: this.isTurkishValue
    })
    if (!ch) return
    event.preventDefault()
    this.insertOttomanCharAtCaret(ch)
    this.echoOttomanKey(ch)
  }

  // The on-screen key for a letter typed on the reader's own keyboard flashes claret.
  echoOttomanKey(ch) {
    if (!ch || !this.hasOttomanSideKeyboardTarget || !this.isOttomanKeyboardOpen()) return
    const key = this.ottomanSideKeyboardTarget.querySelector(`[data-mapped-value="${CSS.escape(ch)}"]`)
    this.lightOttomanKey(key)
  }

  // Restarts the flash if the same key is pressed again before the last one ends.
  lightOttomanKey(key) {
    if (!key) return
    key.classList.remove("is-echo")
    void key.offsetWidth
    key.classList.add("is-echo")
    clearTimeout(key.echoTimer)
    key.echoTimer = setTimeout(() => key.classList.remove("is-echo"), 170)
  }

  // Handles backspace button on on-screen Ottoman keyboard
  ottomanSearchKeyboardBackspaceMouseDown(event) {
    event.preventDefault()
    const input = this.inputTarget
    let selectionStart = input.selectionStart
    let selectionEnd = input.selectionEnd
    const currentValue = input.value
    if (selectionStart !== selectionEnd) {
      input.value = currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd)
      input.setSelectionRange(selectionStart, selectionStart)
    } else if (selectionStart > 0) {
      input.value = currentValue.slice(0, selectionStart - 1) + currentValue.slice(selectionEnd)
      input.setSelectionRange(selectionStart - 1, selectionStart - 1)
    }
    input.dispatchEvent(new Event("input"))
    input.focus()
  }

  // Handles close button on on-screen Ottoman keyboard
  ottomanSearchKeyboardCloseMouseDown(event) {
    event.preventDefault()
    this.hideOttomanSideKeyboard()
    this.inputTarget.focus()
  }
  
  // Toggles OTT/ENG: clears input, updates theme/placeholders, and syncs results visibility.
  handleSourceSwitchClick(event) {
    if (this.denyRestrictedSearchBar(event, { stopPropagation: true })) return

    event.preventDefault()
    event.stopPropagation()

    const nextSource = event.currentTarget.getAttribute("data-source")
    if (!nextSource || nextSource === this.sourceType) return

    if (this.hasInputTarget && this.inputTarget.value.length > 0) {
      this.inputTarget.value = ""
      this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    this.sourceType = nextSource
    this.updateSourceSwitchUI()

    if (this.sourceType === "eng") {
      this.activeMode = "latin"
      this.hideOttomanSideKeyboard()
      searchOutput.hideEngDefinitionIframe()
      this.applyInputDirection()
      this.applyTheme()
      this.updatePlaceholders()
      this.updateSearchTypeField()
      this.applySearchButtonTheme()
      this.inputTarget.focus()
      this.syncSearchResultsVisibility()
      return
    }

    this.activeMode = null
    searchOutput.hideEngDefinitionIframe()
    this.applyTheme()
    this.updatePlaceholders()
    this.updateSearchTypeField()
    this.applySearchButtonTheme()
    this.inputTarget.focus()
    this.syncSearchResultsVisibility()
  }

  // Ottoman dictionary results are irrelevant in ENG (English definitions) mode.
  syncSearchResultsVisibility() {
    const root = this.element.closest(".js-lexiqamus-search-results-root")
    if (!root) return

    const resultsEl = root.querySelector(".js-lexiqamus-search-results")
    if (!resultsEl) return

    // Clear search results list in ENG mode
    if (this.sourceType === "eng") {
      resultsEl.innerHTML = ""
      resultsEl.hidden = true
      return
    }

    const wordDecoderPanel = root.querySelector(".js-search-results-container")
    resultsEl.hidden = wordDecoderPanel ? !wordDecoderPanel.hidden : false
  }

  // Syncs active class and aria-pressed on OTT/ENG toggle buttons.
  updateSourceSwitchUI() {
    if (!this.hasSourceSwitchOptionTarget) return

    this.sourceSwitchOptionTargets.forEach((option) => {
      const isActive = option.getAttribute("data-source") === this.sourceType
      option.classList.toggle("lq-source-switch-option--active", isActive)
      option.setAttribute("aria-pressed", isActive ? "true" : "false")
    })

    if (this.hasSourceSwitchTarget) {
      this.sourceSwitchTarget.classList.toggle("lq-source-switch--eng-active", this.sourceType === "eng")
    }

    if (typeof searchOutput !== "undefined") {
      searchOutput.syncSubscriptionRecommendationButtonTheme(this.sourceType === "eng")
    }
  }

  // Left half = Latin, right half = Ottoman; clears input when switching sides.
  detectClickSide(event) {
    if (this.denyRestrictedSearchBar()) return

    if (event.target.closest(".lq-source-switch")) return
    if (this.sourceType === "eng") return

    const wrapper  = this.wrapperTarget
    const input    = this.inputTarget
    const rect     = wrapper.getBoundingClientRect()
    const clickX   = event.clientX - rect.left
    const clickedSide = clickX < rect.width / 2 ? 'latin' : 'ottoman'

    if (this.activeMode !== null && this.activeMode !== clickedSide) {
      input.value = ''
      // Keep all Stimulus input listeners in sync for programmatic changes.
      input.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this.activeMode = clickedSide
    this.applyInputDirection()
    this.applyTheme()
    this.updatePlaceholders()
    this.updateSearchTypeField()
    this.applySearchButtonTheme()
    this.updateSearchFieldFont(input)

    // Show/hide Ottoman keyboard based on selected side
    if (clickedSide === "ottoman") {
      this.showOttomanSideKeyboard()
    } else {
      this.hideOttomanSideKeyboard()
    }

    input.focus()
    // Only move cursor to RTL start when the field is empty (no existing text to preserve selection for).
    // If there's already Ottoman text, the user may be trying to select/edit it — don't interfere.
    if (this.activeMode === 'ottoman' && input.value.trim().length === 0) {
      this.moveCursorToRtlStart(input)
    }
  }

  // Re-syncs mode, placeholders, and button theme on every keystroke.
  handleInput() {
    if (this.denyRestrictedSearchBar()) return

    this.syncFromInput()
    this.updatePlaceholders()
    this.applySearchButtonTheme()
    this.updateActionButtonsVisibility()
  }

  // Resets to neutral state when input is emptied (unless Ottoman keyboard is open).
  handleBlur() {
    // Prevent blur reset if Ottoman keyboard is open
    if (this.isOttomanKeyboardOpen()) return
    if (this.sourceType === "eng") return

    const input = this.inputTarget
    if (input.value.trim().length === 0) {
      this.activeMode = null
      this.wrapperTarget.classList.remove('search-bar-mode-latin', 'search-bar-mode-ottoman')
      this.updatePlaceholders()
      this.updateSearchTypeField()
      this.hideOttomanSideKeyboard()
      this.applySearchButtonTheme()
    }
  }

  // Infers latin vs ottoman mode from current input text.
  syncFromInput() {
    const input = this.inputTarget
    const text  = input.value.trim()

    if (this.sourceType === "eng") {
      this.activeMode = "latin"
      this.applyInputDirection()
      this.applyTheme()
      this.updateSearchFieldFont(input)
      this.applySearchButtonTheme()
      return
    }

    if (text.length === 0) return

    this.activeMode = isOttomanText(text) ? 'ottoman' : 'latin'
    this.applyInputDirection()
    this.applyTheme()
    this.updateSearchFieldFont(input)
    this.applySearchButtonTheme()
    // Hide Ottoman keyboard when switching to Latin mode
    if (this.activeMode === "latin") {
      this.hideOttomanSideKeyboard()
    }
  }

  // Sets dir/text-align for LTR Latin or RTL Ottoman typing.
  applyInputDirection() {
    const input = this.inputTarget
    if (this.sourceType === "eng" || this.activeMode === 'latin') {
      input.setAttribute('dir', 'ltr')
      input.style.textAlign = 'left'
      input.style.direction  = 'ltr'
    } else if (this.activeMode === 'ottoman') {
      input.setAttribute('dir', 'rtl')
      input.style.textAlign = 'right'
      input.style.direction  = 'rtl'
    }
  }

  // Place cursor at logical start (right side for RTL) so typing starts from the right.
  moveCursorToRtlStart(input) {
    input.setSelectionRange(0, 0)
    // Force reflow so browser applies RTL before selection.
    requestAnimationFrame(() => { input.setSelectionRange(0, 0) })
  }

  // Applies navy (Latin/ENG) or burgundy (Ottoman) border theme on the wrapper.
  applyTheme() {
    const wrapper = this.wrapperTarget
    wrapper.classList.remove('search-bar-mode-latin', 'search-bar-mode-ottoman', 'search-bar-mode-english')
    if (this.sourceType === "eng") {
      wrapper.classList.add('search-bar-mode-latin', 'search-bar-mode-english')
      return
    }
    if (this.activeMode === 'latin') {
      wrapper.classList.add('search-bar-mode-latin')
    } else if (this.activeMode === 'ottoman') {
      wrapper.classList.add('search-bar-mode-ottoman')
    }
  }

  // Shows the placeholder for the active side; hides all when text is present.
  updatePlaceholders() {
    if (!this.hasInputTarget) return

    const input   = this.inputTarget
    const hasText = input.value.length > 0

    if (this.sourceType === "eng") {
      if (this.hasPlaceholderEnglishTarget) {
        this.placeholderEnglishTarget.classList.toggle('search-bar-placeholder-hidden', hasText)
        this.placeholderEnglishTarget.classList.toggle('d-none', false)
      }
      if (this.hasPlaceholderLatinTarget) {
        this.placeholderLatinTarget.classList.add('search-bar-placeholder-hidden', 'd-none')
      }
      if (this.hasPlaceholderOttomanTarget) {
        this.placeholderOttomanTarget.classList.add('search-bar-placeholder-hidden', 'd-none')
      }
      return
    }

    if (this.hasPlaceholderEnglishTarget) {
      this.placeholderEnglishTarget.classList.add('search-bar-placeholder-hidden', 'd-none')
    }
    if (this.hasPlaceholderLatinTarget) {
      this.placeholderLatinTarget.classList.remove('d-none')
    }
    if (this.hasPlaceholderOttomanTarget) {
      this.placeholderOttomanTarget.classList.remove('d-none')
    }

    if (hasText) {
      this.placeholderLatinTarget.classList.add('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.add('search-bar-placeholder-hidden')
      return
    }

    if (this.activeMode === 'latin') {
      this.placeholderLatinTarget.classList.add('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.remove('search-bar-placeholder-hidden')
    } else if (this.activeMode === 'ottoman') {
      this.placeholderLatinTarget.classList.remove('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.add('search-bar-placeholder-hidden')
    } else {
      this.placeholderLatinTarget.classList.remove('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.remove('search-bar-placeholder-hidden')
    }
  }

  // Hidden field: "definition" for ENG, or active latin/ottoman side for OTT.
  updateSearchTypeField() {
    if (!this.hasSearchTypeFieldTarget) return
    if (this.sourceType === "eng") {
      this.searchTypeFieldTarget.value = "definition"
      return
    }
    this.searchTypeFieldTarget.value = this.activeMode || ""
  }

  // Search button stays burgundy in OTT mode; Latin styling only for ENG.
  applySearchButtonTheme() {
    if (!this.hasSearchButtonTarget) return

    this.searchButtonTarget.classList.remove('search-button-script-latin', 'search-button-script-ottoman')
    if (this.sourceType === "eng") {
      this.searchButtonTarget.classList.add('search-button-script-latin')
      return
    }
    // OTT mode: burgundy button; latin/ottoman side only changes the input border.
    this.searchButtonTarget.classList.add('search-button-script-ottoman')
  }

  // ENG only: intercepts form submit and loads definition search in the iframe.
  handleFormSubmit(event) {
    if (this.denyRestrictedSearchBar(event)) return

    if (this.sourceType !== "eng") return

    event.preventDefault()
    const searchedText = this.inputTarget.value.trim()
    if (searchedText.length === 0) return

    searchOutput.loadEngDefinitionSearchInIframe(searchedText)
    this.syncSearchResultsVisibility()
    this.updateActionButtonsVisibility()
  }

  // True when Search is blocked via allow_search (js-search-bar-restricted on the root).
  isSearchBarRestricted() {
    return this.element.classList.contains("js-search-bar-restricted")
  }

  // Stops the interaction and shows the denial message when Search is restricted.
  // Returns true so callers can early-return without repeating the guard.
  denyRestrictedSearchBar(event = null, { stopPropagation = false, stopImmediate = false } = {}) {
    if (!this.isSearchBarRestricted()) return false

    if (event) {
      event.preventDefault()
      if (stopPropagation) event.stopPropagation()
      if (stopImmediate) event.stopImmediatePropagation()
    }
    this.showSearchBarDeniedMessage()
    return true
  }

  // Dims the search bar chrome for restricted Search (Word Decoder stays usable).
  applySearchBarRestrictedAppearance() {
    const searchBarWrapper = this.element.querySelector(".js-search-bar-wrapper")
    if (searchBarWrapper) searchBarWrapper.classList.add("search-bar-wrapper--restricted")
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.add("search-bar-input-wrapper--restricted")
    }
    if (this.hasSourceSwitchTarget) {
      this.sourceSwitchTarget.classList.add("lq-source-switch--restricted")
    }
  }

  // Focus/click entry points on the input wrapper when Search is restricted.
  handleSearchBarRestrictedInteraction() {
    this.denyRestrictedSearchBar()
  }

  // Reveals the denial HTML, dims the bar, and disables further Search input.
  showSearchBarDeniedMessage() {
    if (this.hasDeniedMessageTarget) {
      this.deniedMessageTarget.classList.remove("d-none")
    }

    this.applySearchBarRestrictedAppearance()
    this.hideOttomanSideKeyboard()

    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.readOnly = true
      this.inputTarget.setAttribute("aria-disabled", "true")
    }
    if (this.hasSearchButtonTarget) {
      this.searchButtonTarget.disabled = true
    }
  }

  // Applies Ottoman or Latin font class based on typed script.
  updateSearchFieldFont(field) {
    const text = field.value.trim()
    field.classList.remove('search-field-ottoman', 'search-field-latin')
    if (text.length === 0) return

    if (this.sourceType === "eng") {
      field.classList.add('search-field-latin')
      return
    }

    field.classList.add(isOttomanText(text) ? 'search-field-ottoman' : 'search-field-latin')
  }

  // Blocks empty submit; ENG opens definition search instead of form POST.
  handleSearchButtonClick(event) {
    if (this.denyRestrictedSearchBar(event, { stopImmediate: true })) return

    if (!this.hasInputTarget) return
    if (this.inputTarget.value.trim().length === 0) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    if (this.sourceType === "eng") {
      event.preventDefault()
      event.stopImmediatePropagation()
      searchOutput.loadEngDefinitionSearchInIframe(this.inputTarget.value.trim())
      this.syncSearchResultsVisibility()
      this.updateActionButtonsVisibility()
    }
  }

  // Clears search field and results — navigates to the bare search page (LexiQamus lqNewSearch).
  handleNewSearch(event) {
    event.preventDefault()
    window.location.assign(this.buildNewSearchUrl())
  }

  // True when the search bar has non-empty input text.
  hasInputText() {
    return this.hasInputTarget && this.inputTarget.value.trim().length > 0
  }

  // Builds the target URL for a new search reset, preserving locale and active tab.
  buildNewSearchUrl() {
    const basePath = this.hasNewSearchUrlValue ? this.newSearchUrlValue : window.location.pathname
    const url = new URL(basePath, window.location.origin)
    const locale = new URLSearchParams(window.location.search).get("locale")
    if (locale) url.searchParams.set("locale", locale)
    if (this.isWordDecoderTabActive()) url.searchParams.set("word_decoder", "1")
    return url.toString()
  }

  // True when the Word Decoder tab is active on the LexiQamus search results page.
  isWordDecoderTabActive() {
    const root = this.element.closest(".js-lexiqamus-search-results-root")
    return Boolean(root?.querySelector('[data-target="word-decoder"].lq-mode-tab--active'))
  }

  // True when the Search mode tab is active on LexiQamus (absent on search_output_modified).
  isSearchModeTabActive() {
    const root = this.element.closest(".js-lexiqamus-search-results-root")
    return Boolean(root?.querySelector('[data-target="search"].lq-mode-tab--active'))
  }

  // Copies a shareable search URL to the clipboard (LexiQamus "Share link").
  handleShareLink(event) {
    event.preventDefault()
    this.copyToClipboard(this.buildShareLinkUrl())
    this.showShareLinkCopiedFeedback()
  }

  // Ensures Word Decoder searches include word_decoder=1 so pasted links reopen the correct tab.
  // ENG iframe searches do not update the browser URL, so encode searched_text + search_type=definition here.
  buildShareLinkUrl() {
    const currentUrl = new URL(window.location.href)

    if (this.sourceType === "eng" && this.hasInputTarget) {
      const searchedText = this.inputTarget.value.trim()
      if (searchedText.length > 0) {
        const shareUrl = new URL(currentUrl.pathname, currentUrl.origin)
        const locale = currentUrl.searchParams.get("locale")
        if (locale) shareUrl.searchParams.set("locale", locale)
        shareUrl.searchParams.set("searched_text", searchedText)
        shareUrl.searchParams.set("search_type", "definition")
        return shareUrl.toString()
      }
    }

    const isBoxSearch = currentUrl.searchParams.get("search_type") === "box"

    if (isBoxSearch || this.isWordDecoderTabActive()) {
      currentUrl.searchParams.set("word_decoder", "1")
    }

    return currentUrl.toString()
  }

  // Reopens ENG mode and reloads the definition iframe when a shared link includes search_type=definition.
  restoreEngDefinitionSearchFromUrl() {
    const params = new URLSearchParams(window.location.search)
    if (params.get("search_type") !== "definition") return false

    const searchedText = (params.get("searched_text") || this.inputTarget?.value || "").trim()
    if (!searchedText) return false

    this.sourceType = "eng"
    this.activeMode = "latin"
    this.updateSourceSwitchUI()
    this.applyInputDirection()
    this.applyTheme()
    this.updatePlaceholders()
    this.updateSearchTypeField()
    this.applySearchButtonTheme()
    this.syncSearchResultsVisibility()

    if (typeof searchOutput !== "undefined") {
      searchOutput.loadEngDefinitionSearchInIframe(searchedText)
    }

    return true
  }

  // Briefly updates the button label after copy, matching search-output#showCopySuccess.
  showShareLinkCopiedFeedback() {
    if (!this.hasShareLinkButtonTarget) return

    const button = this.shareLinkButtonTarget
    const originalText = button.textContent.trim()
    const copiedLabel = this.hasCopiedTextValue ? this.copiedTextValue : "Copied!"

    button.textContent = copiedLabel

    if (this.shareLinkResetTimeoutId) clearTimeout(this.shareLinkResetTimeoutId)
    this.shareLinkResetTimeoutId = setTimeout(() => {
      button.textContent = originalText
      this.shareLinkResetTimeoutId = null
    }, 4000)
  }

  // Shows the action row for New Search on input/content; Share Link only after a completed search.
  // LexiQamus Search tab never shows these buttons; Word Decoder and search_output_modified keep prior rules.
  updateActionButtonsVisibility() {
    if (!this.hasActionButtonsTarget) return

    let shouldShowSection
    if (this.isSearchModeTabActive()) {
      shouldShowSection = false
    } else if (this.isWordDecoderTabActive()) {
      shouldShowSection = this.hasDecoderContent() || this.pageHasSearchResults()
    } else {
      shouldShowSection = this.hasInputText() || this.pageHasSearchResults()
    }

    this.actionButtonsTarget.classList.toggle("search-bar-actions-section--hidden", !shouldShowSection)
    this.element.classList.toggle("search-bar-has-actions", shouldShowSection)

    if (this.hasShareLinkButtonTarget) {
      this.shareLinkButtonTarget.classList.toggle(
        "search-bar-btn-share-link--hidden",
        !this.hasCompletedSearch()
      )
    }
  }

  // True after the user has run a search (OTT, ENG iframe, or Word Decoder), not merely typed input.
  hasCompletedSearch() {
    if (this.isWordDecoderTabActive()) {
      if (!this.pageHasSearchResults()) return false
      return this.wordDecoderQueryMatchesCompletedSearch()
    }

    if (this.sourceType === "eng") {
      if (!this.hasEngDefinitionResultsVisible()) return false
      return this.engQueryMatchesCompletedSearch()
    }

    if (!this.pageHasSearchResults()) return false
    return this.ottQueryMatchesCompletedSearch()
  }

  // OTT search tab: input must still match the searched_text in the URL.
  ottQueryMatchesCompletedSearch() {
    if (!this.hasInputTarget) return false

    const urlSearchedText = (new URLSearchParams(window.location.search).get("searched_text") || "").trim()
    if (urlSearchedText.length === 0) return false

    return this.inputTarget.value.trim() === urlSearchedText
  }

  // ENG iframe search: input must match the term currently loaded in the definition iframe.
  engQueryMatchesCompletedSearch() {
    if (!this.hasInputTarget) return false

    const currentInput = this.inputTarget.value.trim()
    if (currentInput.length === 0) return false
    if (typeof searchOutput === "undefined" || !searchOutput.hasDefinitionIframeTarget) return false

    const iframeSrc = searchOutput.definitionIframeTarget.src
    if (!iframeSrc || iframeSrc === "about:blank") return false

    try {
      const iframeWord = (new URL(iframeSrc).searchParams.get("_word") || "").trim()
      return currentInput === iframeWord
    } catch {
      return false
    }
  }

  // Word Decoder: grid pattern must still match the box search in the URL.
  wordDecoderQueryMatchesCompletedSearch() {
    const params = new URLSearchParams(window.location.search)
    const urlPattern = (params.get("searched_text") || "").trim()
    if (urlPattern.length === 0 || params.get("search_type") !== "box") return false

    const decoder = this.wordDecoderController()
    if (!decoder) return true

    return decoder.buildSearchPattern() === urlPattern
  }

  wordDecoderController() {
    const root = this.element.closest(".js-lexiqamus-search-results-root")
    if (!root) return null

    const el = root.matches("[data-controller~='lexiqamus-word-decoder']")
      ? root
      : root.querySelector("[data-controller~='lexiqamus-word-decoder']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "lexiqamus-word-decoder") : null
  }

  // True when the ENG definition iframe results panel is visible below the search bar.
  hasEngDefinitionResultsVisible() {
    if (typeof searchOutput === "undefined" || !searchOutput.hasDefinitionIframeContainerTarget) return false
    return !searchOutput.definitionIframeContainerTarget.hidden
  }

  // True when any Word Decoder letter input in the page root has content.
  hasDecoderContent() {
    const root = this.element.closest(".js-lexiqamus-search-results-root")
    if (!root) return false
    return Array.from(root.querySelectorAll(".js-word-decoder-letter-input")).some((input) => {
      if (input.dataset.char) return true
      return (input.value || "").trim().length > 0
    })
  }

  // Returns true when the page currently shows search results or a no-results card.
  pageHasSearchResults() {
    const params = new URLSearchParams(window.location.search)
    if (params.get("searched_text")) return true

    const pageRoot = this.element.closest(".js-lexiqamus-search-results-root")
      || document.querySelector("[data-controller~='search-output']")
    if (!pageRoot) return false

    // True when real OTT results or a no-results card is on the page (excludes the hidden ENG iframe shell so empty home does not show action buttons).
    return Boolean(
      pageRoot.querySelector(".js-search-results-card, .js-search-no-results-card")
    )
  }
}
